import { createElement } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { useSetContextualNav } from '@/components/layout/nav-context';
import { useWafServiceDetail, WafServiceAccessError } from '@/api/waf';
import { categoryBackTarget } from '../categories';
import { WAF_NAV } from './waf-nav';

// WAF lives under the Security category landing — send "Back" there.
const WAF_BACK = categoryBackTarget('security');
import { WafDomainList } from './domains/WafDomainList';
import { WafDomainDetail } from './domains/WafDomainDetail';
import { WafSslList } from './ssl/WafSslList';
import { WafCustomPageList } from './custom-pages/WafCustomPageList';
import { WafDnsRecordList } from './dns/WafDnsRecordList';
import { WafDnsAnalytics } from './analytics/WafDnsAnalytics';
import { WafEdgeAnalytics } from './analytics/WafEdgeAnalytics';
import { WafPulseAnalytics } from './analytics/WafPulseAnalytics';
import { WafOriginShieldAnalytics } from './analytics/WafOriginShieldAnalytics';
import { WafMultiCdnAnalytics } from './analytics/WafMultiCdnAnalytics';
import { WafRoutingStrategies } from './analytics/WafRoutingStrategies';
import { WafStrategyForm } from './analytics/WafStrategyForm';
import { WafRouteMap } from './routing/WafRouteMap';
import { WafDataFeeds } from './datafeeds/WafDataFeeds';
import { WafMonitoringCdn } from './monitors/WafMonitoringCdn';
import { WafMonitorForm } from './monitors/WafMonitorForm';
import { WafDashboard } from './home/WafDashboard';
import { WafBulkConfigure } from './bulk/WafBulkConfigure';
import { WafAddSiteWizard } from './sites/WafAddSiteWizard';
import { WafCacheControl } from './cache/WafCacheControl';
import { WafOriginProtocol } from './origin/WafOriginProtocol';
import { WafCdnManagement } from './cdn/WafCdnManagement';
import { WafBotManagement } from './bot/WafBotManagement';
import { WafAccountTakeover } from './atp/WafAccountTakeover';
import { WafAccountTakeoverForm } from './atp/WafAccountTakeoverForm';
import { WafCrsRules } from './rules/WafCrsRules';
import { WafAccessControl } from './access-control/WafAccessControl';
import { WafFirewallRules } from './firewall/WafFirewallRules';
import { WafFirewallRuleForm } from './firewall/WafFirewallRuleForm';
import { WafOriginRules } from './origin/WafOriginRules';
import { WafOriginRuleForm } from './origin/WafOriginRuleForm';
import { WafEdgeRules } from './edge-rules/WafEdgeRules';
import { WafEdgeRuleForm } from './edge-rules/WafEdgeRuleForm';
import { WafApiProtection } from './api-protection/WafApiProtection';
import { WafApiProtectionForm } from './api-protection/WafApiProtectionForm';
import { WafApiProtectionDetail } from './api-protection/WafApiProtectionDetail';

/** Longest nav slug that prefixes the current sub-path → active sidebar entry. */
function activeSlug(subPath: string): string {
  const slugs = WAF_NAV.flatMap((g) => g.entries.map((e) => e.slug));
  let best = '';
  for (const slug of slugs) {
    if ((subPath === slug || subPath.startsWith(`${slug}/`)) && slug.length > best.length) best = slug;
  }
  return best;
}

/**
 * Owl/Mlytics WAF console — per provisioned service (`External.Mlytics`). Thin
 * shell: registers the contextual sidebar, guards access (403/404 → bounce to
 * Services), and mounts the sub-routes. Each sub-page supplies its own chrome.
 */
export function WafConsole() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { serviceId = '' } = useParams<{ serviceId: string }>();
  const service = useWafServiceDetail(serviceId);
  const base = `/services/waf/${serviceId}`;
  const hrefFor = (slug: string) => `${base}/${slug}`;

  useSetContextualNav(() => {
    const sub = pathname.slice(base.length).replace(/^\//, '');
    const active = activeSlug(sub);
    return {
      sections: WAF_NAV.map((group) => ({
        heading: group.heading,
        items: group.entries.map((e) => ({
          id: hrefFor(e.slug),
          label: e.label,
          icon: createElement(e.icon, { size: 16 }),
          disabled: e.disabled,
          badge: e.badge,
          onSelect: e.disabled ? undefined : () => navigate(hrefFor(e.slug)),
        })),
      })),
      activeId: active ? hrefFor(active) : undefined,
      back: { label: WAF_BACK.label, onSelect: () => navigate(WAF_BACK.path) },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, pathname, navigate]);

  // No access / no such service (hard HTTP or soft 200-body status) → dashboard.
  const errStatus =
    service.error instanceof WafServiceAccessError
      ? service.error.status
      : isAxiosError(service.error)
        ? service.error.response?.status
        : undefined;
  if (errStatus === 403 || errStatus === 404) return <Navigate to="/services" replace />;

  return (
    <Routes>
      <Route index element={<Navigate to={`${base}/domains`} replace />} />
      <Route path="domains" element={<WafDomainList serviceId={serviceId} />} />
      <Route path="sites/new" element={<WafAddSiteWizard serviceId={serviceId} />} />
      <Route path="domains/bulk" element={<WafBulkConfigure serviceId={serviceId} />} />
      {/* Detail takes the (dotted) domain via query param, not a path segment —
          a dotted last path segment makes the dev server's history fallback 404
          on hard refresh / deep-link. */}
      <Route path="domains/view" element={<WafDomainDetail serviceId={serviceId} />} />
      <Route path="ssl" element={<WafSslList serviceId={serviceId} />} />
      <Route path="custom-pages" element={<WafCustomPageList serviceId={serviceId} />} />
      <Route path="rules" element={<WafCrsRules serviceId={serviceId} />} />
      <Route path="access-control" element={<WafAccessControl serviceId={serviceId} />} />
      <Route path="firewall" element={<WafFirewallRules serviceId={serviceId} />} />
      <Route path="firewall/new" element={<WafFirewallRuleForm serviceId={serviceId} />} />
      <Route path="firewall/edit" element={<WafFirewallRuleForm serviceId={serviceId} />} />
      <Route path="api-protection" element={<WafApiProtection serviceId={serviceId} />} />
      <Route path="api-protection/detail" element={<WafApiProtectionDetail serviceId={serviceId} />} />
      <Route path="api-protection/new" element={<WafApiProtectionForm serviceId={serviceId} />} />
      <Route path="api-protection/edit" element={<WafApiProtectionForm serviceId={serviceId} />} />
      <Route path="bot-management" element={<WafBotManagement serviceId={serviceId} />} />
      <Route path="account-takeover" element={<WafAccountTakeover serviceId={serviceId} />} />
      <Route path="account-takeover/new" element={<WafAccountTakeoverForm serviceId={serviceId} />} />
      <Route path="account-takeover/edit" element={<WafAccountTakeoverForm serviceId={serviceId} />} />
      <Route path="dns-records" element={<WafDnsRecordList serviceId={serviceId} />} />
      <Route path="dns-requests" element={<WafDnsAnalytics serviceId={serviceId} />} />
      {/* Back-compat: the DNS chart used to live at /dns under Analytics. */}
      <Route path="dns" element={<Navigate to={`${base}/dns-requests`} replace />} />
      <Route path="experience" element={<WafEdgeAnalytics serviceId={serviceId} />} />
      <Route path="pulse" element={<WafPulseAnalytics serviceId={serviceId} />} />
      <Route path="origin-shield" element={<WafOriginShieldAnalytics serviceId={serviceId} />} />
      <Route path="multi-cdn" element={<WafMultiCdnAnalytics serviceId={serviceId} />} />
      <Route path="routing" element={<WafRoutingStrategies serviceId={serviceId} />} />
      <Route path="routing/new" element={<WafStrategyForm serviceId={serviceId} />} />
      <Route path="routing/edit" element={<WafStrategyForm serviceId={serviceId} />} />
      <Route path="route-map" element={<WafRouteMap serviceId={serviceId} />} />
      <Route path="data-feeds" element={<WafDataFeeds serviceId={serviceId} />} />
      <Route path="monitors" element={<WafMonitoringCdn serviceId={serviceId} />} />
      <Route path="monitors/new" element={<WafMonitorForm serviceId={serviceId} />} />
      <Route path="monitors/edit" element={<WafMonitorForm serviceId={serviceId} />} />
      <Route path="overview" element={<WafDashboard serviceId={serviceId} />} />
      <Route path="cache" element={<WafCacheControl serviceId={serviceId} />} />
      <Route path="origin-protocol" element={<WafOriginProtocol serviceId={serviceId} />} />
      <Route path="cdn" element={<WafCdnManagement serviceId={serviceId} />} />
      <Route path="origin" element={<WafOriginRules serviceId={serviceId} />} />
      <Route path="origin/new" element={<WafOriginRuleForm serviceId={serviceId} />} />
      <Route path="origin/edit" element={<WafOriginRuleForm serviceId={serviceId} />} />
      <Route path="edge-rules" element={<WafEdgeRules serviceId={serviceId} />} />
      <Route path="edge-rules/new" element={<WafEdgeRuleForm serviceId={serviceId} />} />
      <Route path="edge-rules/edit" element={<WafEdgeRuleForm serviceId={serviceId} />} />
      {/* Absolute redirect: a relative target would compound on each unmatched hop. */}
      <Route path="*" element={<Navigate to={`${base}/domains`} replace />} />
    </Routes>
  );
}

export default WafConsole;
